import { withDialog } from "../compat/jquery-ui-dialog";
/**
 * SubmitViaThemeNativeSubmit — the drawer submitter (REQ-CHK-9; research/05
 * §2.4, §8.10, §8.11). Verbatim from v1 checkout_helper.js: double token
 * guard (soft warn+return, then hard #errDialog on the re-read),
 * data-cpc-temp hidden-input injection (_cpc_properties always; id/token/
 * quantity/visible calc-props only when the calculator is outside the form —
 * quantity selected by HTML ATTRIBUTE here, :hidden SKIPPED here, no
 * cpc_upload_ handling here), in-form isQuantity name swap (DIV-013) +
 * in-form slider name swap + restore, setTimeout(cleanup, 500) race,
 * requestSubmit() with synthetic-event fallback, loader hide, no redirect.
 *
 * For new checkout flow (Shopify Functions): inject CPC data into the form and
 * fire the form's native submit event so the THEME handles /cart/add.js +
 * drawer opening. This is theme-agnostic — every theme's own submit handler
 * fires naturally.
 *
 * all jQuery is the BUNDLED copy (DIV-004).
 */
import $ from "jquery";
import { appState } from "../state/app-state";
import {
  GetVariantProperties,
  quantityPropertyNames,
  stripHiddenLookupFormulaAxes,
  collectExternalLineProperties,
  formatSliderValueForCart,
  isSliderInput,
} from "./variant-properties";
import { hasTranslations, t, translateCartProperties, translatePropertyName } from "../i18n/translate";

export function SubmitViaThemeNativeSubmit(calculatorElement: any = null): void {
  // Block submission if the price token hasn't resolved from the /variant API
  if (appState.cpc_isNewCheckoutFlowEnabled) {
    var tokenEl = document.getElementById("cpcPriceToken") as any;
    if (!tokenEl || !tokenEl.value || tokenEl.value === "none") {
      console.warn("Still preparing your price. Please wait a moment and try again.");
      $(".loading-anim-cpc").hide();
      return;
    }
  }

  var formm: any = appState.productForm;
  var injectedInputs: any[] = [];
  var slidersToRestore: any[] = [];

  // Guard: abort if the price token is still "none" (unresolved).
  // Without a valid cpc_ token the Cart Transform function at checkout has
  // nothing to look up and silently falls back to the base variant price.
  var tokenEl = document.getElementById("cpcPriceToken") as any;
  var tokenValue = tokenEl ? tokenEl.value : null;
  if (!tokenValue || tokenValue === "none") {
    console.error(
      "CPC: SubmitViaThemeNativeSubmit called with missing/invalid price token — aborting to prevent wrong price at checkout. Token:",
      tokenValue,
    );
    $(".loading-anim-cpc").hide();
    $("#errDialog").attr("title", "Error");
    $("#dialogMsg").text(t("Something went wrong calculating your price. Please try again."));
    withDialog(() => ($("#errDialog") as any).dialog({ modal: true }));
    return;
  }

  function injectInput(name: any, value: any) {
    var input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.setAttribute("data-cpc-temp", "true");
    formm.appendChild(input);
    injectedInputs.push(input);
  }

  function cleanup() {
    injectedInputs.forEach(function (el) {
      if (el.parentNode) el.parentNode.removeChild(el);
    });
    // Restore the names we swapped off in-form isQuantity elements and sliders
    // (and, with translations on, every in-form input we swapped) so the
    // control keeps working after the submit (and on a second add-to-cart
    // without a page reload).
    slidersToRestore.forEach(function (s) {
      s.el.setAttribute("name", s.name);
    });
  }

  // Inject serialized calculator properties.
  // Strip Shopify-internal and upload metadata keys before stringifying.
  // The Cart Transform function already filters these out post-parse, but
  // including them in _cpc_properties inflates the per-line CT input payload.
  // At 15+ cart lines the combined payload can exceed Shopify's CT size limit,
  // causing silent fallback to the base variant price. Removing them here
  // keeps the blob small without losing any data the CT function actually needs.
  var cpc_Properties = GetVariantProperties();
  // DIV-010: the "Use as Quantity" element is the line quantity (injected below
  // on the App Block path), not a line attribute — see quantityPropertyNames().
  var quantityKeys = quantityPropertyNames();
  const cpcPropsFiltered = Object.fromEntries(
    Object.entries(Object.assign({}, cpc_Properties)).filter(
      ([key]) =>
        !key.startsWith('shopify_') &&
        !key.startsWith('cpc_upload_') &&
        quantityKeys.indexOf(key) === -1
    )
  );
  // Issue #83: a Lookup element's "isformula" axis is a permanently-hidden
  // formula-feed value (never meant to be shopper-visible), but GVP includes
  // it for /variant pricing — see stripHiddenLookupFormulaAxes().
  stripHiddenLookupFormulaAxes(cpcPropsFiltered);
  // Crisp session_42d55424 (LiquidDreamsDesign/Uploadly): the Cart Transform
  // function rebuilds the line's attributes from THIS blob alone, so a field
  // another app or the theme already wrote onto the form (not a .calc-prop)
  // must ride along inside it or it is silently dropped at checkout. Collected
  // BEFORE any in-form isQuantity/slider/translation swap-off below, while
  // every such CPC-owned element still carries its real `.calc-prop`/
  // `cpc-slider`/`data-cpc_isquantity` marker — collectExternalLineProperties()
  // excludes all three, so none of them are ever mistaken for an external
  // field. (Injecting here, in the original position, also keeps the
  // data-cpc-temp injection ORDER goldens below already pin unchanged.) Merge
  // UNDER the calculator's own properties so a same-named CPC field always wins.
  const mergedProps = Object.assign({}, collectExternalLineProperties(formm), cpcPropsFiltered);
  // Translations v1: the blob's keys become the checkout/order line attributes,
  // so they are the shopper's language; GetVariantProperties() above already
  // read the canonical names for the pricing channel.
  injectInput("properties[_cpc_properties]", JSON.stringify(translateCartProperties(mergedProps)));

  // App Block: calculator is outside the form — inject required fields into form
  if (calculatorElement && !formm.contains(calculatorElement)) {
    // Variant ID
    var variantIdEl = document.getElementById("variantIdCustom") as any;
    if (variantIdEl && variantIdEl.value && variantIdEl.value !== "0") {
      injectInput("id", variantIdEl.value);
    }

    // CPC price token — already validated above, safe to inject
    injectInput("properties[_cpc_custom_cart_item]", tokenValue);

    // Quantity from cpc_isquantity element.
    // Issue #111: when multiple isQuantity elements exist (different conditional
    // branches), use only the one that is currently visible. querySelector picks
    // the first in DOM order regardless of visibility — use querySelectorAll and
    // filter to the first visible match instead.
    var qtyEl: Element | null = null;
    calculatorElement.querySelectorAll('[data-cpc_isquantity="true"]').forEach(function (el: any) {
      if (!qtyEl && !$(el).is(":hidden")) qtyEl = el;
    });
    if (qtyEl) {
      // BUG-107 / DIV-008: a Calculation ("Calculated figure") element flagged
      // "Use as Quantity" carries the flag on a visible <label>/<span> whose
      // computed value is in data-val — its .value lives on a sibling
      // type=hidden input that jQuery :hidden always rejects. Fall back to
      // data-val when the visible node has no usable .value; NumberInputNew is
      // unchanged (its .value is always the source).
      var q = qtyEl as any;
      var qtyRaw = q.value != null && q.value !== "" ? q.value : q.getAttribute("data-val");
      if (qtyRaw != null && qtyRaw !== "") {
        injectInput("quantity", qtyRaw);
      }
    }

    // Calculator properties
    calculatorElement.querySelectorAll(".calc-prop").forEach(function (input: any) {
      if ($(input).is(":hidden")) return;
      if ($(input).data("cpc_isquantity")) return;
      // Issue #110: lookup axis inputs are internal computation helpers.
      // Inject them with a "_" prefix so Shopify hides them from customers.
      if ($(input).data("lookup") !== undefined) {
        const calcname: string = input.dataset.calcname || "";
        if (calcname) injectInput("properties[_" + calcname + "]", input.value);
        return;
      }

      var value = input.value;
      if (input.type === "radio" && !input.checked) return;
      if (input.type === "checkbox") value = input.checked ? "Checked" : "Unchecked";
      else if (isSliderInput(input)) value = formatSliderValueForCart(input);
      else if (input.type === "number") {
        // Issue #ScatterMats: round NumberInputNew values to maxdecimals
        var rawFloat = parseFloat(input.value);
        if (!isNaN(rawFloat)) {
          var maxDec = parseInt(input.getAttribute("data-maxdecimals") || "0", 10);
          if (isNaN(maxDec)) maxDec = 0;
          value = rawFloat.toFixed(maxDec);
        }
      }

      var name = input.name;
      if (!name.startsWith("properties[")) {
        name = "properties[" + (input.dataset.calcname || input.name) + "]";
      }
      // Translations v1: the injected entries are the cart line's visible
      // attributes — swap the key and a choice value (identity when none).
      if (name && value) injectInput(translatePropertyName(name), t(value));
    });
  }

  // In-form isQuantity elements (DIV-013): the theme's native requestSubmit() below
  // serializes every named form field verbatim, including the isQuantity element's
  // OWN properties[Name] field — DIV-010 only taught the App Block branch above and
  // the _cpc_properties blob to drop this key, never the in-form path, so a drawer
  // store with an in-form calculator still posts a real, immutable "Quantity: N"
  // line-item property that the cart-page stepper can never update afterwards
  // (Crisp session_6a3a8fb2, ACT Foam & Rubber — recurrence after DIV-010). Swap the
  // name off before requestSubmit(), same manoeuvre as the slider swap below;
  // nothing is re-injected under it — the Shopify line quantity travels separately.
  // The attribute selector (not `.calc-prop`) also catches a Calculation element's
  // hidden value input, which never carries the calc-prop class (DIV-008).
  // cleanup() restores the name so a second add-to-cart still works.
  if (!calculatorElement || formm.contains(calculatorElement)) {
    formm.querySelectorAll('[data-cpc_isquantity="true"]').forEach(function (input: any) {
      var realName: string = input.name || "";
      if (!realName || !realName.startsWith("properties[")) return;
      input.removeAttribute("name");
      slidersToRestore.push({ el: input, name: realName });
    });
  }

  // In-form sliders: the theme's native submit serializes the range input's raw
  // numeric value. Swap its name off and inject a hidden formatted property under
  // the real name so the cart line item carries the unit (CPCHQ #13). cleanup()
  // restores the names. Outside-form sliders are already handled by the App Block
  // injection loop above.
  if (!calculatorElement || formm.contains(calculatorElement)) {
    formm.querySelectorAll(".cpc-slider").forEach(function (slider: any) {
      if (!slider.name || !slider.name.startsWith("properties[")) return;
      if ($(slider).is(":hidden")) return;
      var realName = slider.name;
      slider.removeAttribute("name");
      slidersToRestore.push({ el: slider, name: realName });
      // Translations v1: the injected entry IS the cart line the shopper reads, so it carries
      // the translated key (identity when there is no translation). The loop below skips
      // sliders precisely because they are handled here.
      injectInput(translatePropertyName(realName), formatSliderValueForCart(slider));
    });
  }

  // Translations v1 (in-form calculators): the theme serializes each input under
  // its canonical `properties[Name]` — the pricing key — which is what the cart
  // page would show. Same manoeuvre as the slider swap above: take the name off
  // the visible input, inject the translated key with the translated choice
  // value, restore in cleanup(). Sliders are already swapped; lookups and
  // underscore-prefixed properties are hidden from the shopper and stay as-is.
  // isQuantity elements are already swapped above (DIV-013) — the selector below
  // only matches inputs that still have a `properties[...]` name, so they don't
  // reach here a second time.
  if (hasTranslations() && (!calculatorElement || formm.contains(calculatorElement))) {
    // Every node the theme would serialize under properties[...] — not just .calc-prop, which
    // misses the Calculation element's sibling type=hidden input (the node that actually
    // carries the computed figure into the cart).
    formm.querySelectorAll('[name^="properties["]').forEach(function (input: any) {
      var realName: string = input.name || "";
      if (isSliderInput(input) || $(input).data("lookup") !== undefined) return;
      if ((input.type === "radio" || input.type === "checkbox") && !input.checked) return;

      // Branch state, DIV-008 style: jQuery :hidden ALWAYS matches a type=hidden input, so for
      // those read the inline display of the sibling .element wrapper — the exact node
      // ProcessConditionalElements sets — instead of the input itself.
      var branchHidden: boolean;
      if (input.type === "hidden") {
        var wrapper = input.previousElementSibling as HTMLElement | null;
        branchHidden = !!wrapper && wrapper.style.display === "none";
      } else {
        branchHidden = $(input).is(":hidden");
      }
      if (branchHidden) return;

      var value = input.value;
      if (input.tagName === "SELECT") {
        var selected = input.options[input.selectedIndex];
        if (selected && selected.dataset.label) value = selected.dataset.label;
      }
      // A Calculation element renders its figure on a <label class='calc-prop'> that also
      // carries the properties[...] name but has no .value. Injecting from it would post the
      // string "undefined" beside the real hidden input's value. An EMPTY string is a real
      // value (an untouched text field) and still gets its key translated — skipping it would
      // leave one English key on an otherwise translated cart line.
      if (typeof value !== "string") return;

      var translatedName = translatePropertyName(realName);
      var translatedValue = t(value);
      if (translatedName === realName && translatedValue === value) return;
      input.removeAttribute("name");
      slidersToRestore.push({ el: input, name: realName });
      injectInput(translatedName, translatedValue);
    });
  }

  // Clean up injected inputs after the theme has read the FormData synchronously
  setTimeout(cleanup, 500);

  // Fire the form's native submit — theme handles /cart/add.js + opening its own drawer.
  // DIV-041: an Alpine.js theme adds to cart from the BUTTON's click handler and
  // never listens for submit; for it the click is re-dispatched with CPC's own
  // capture-phase listener lifted for the duration (window.cpc.addToCartHandler —
  // read off the window, not imported, to keep this module out of the
  // checkout import cycle). The theme reads the form, injected inputs included.
  const alpineButton = window.cpc && window.cpc.alpineSubmitButton === true ? (window.cpc.submitButton as HTMLElement | undefined) : undefined;
  if (alpineButton) {
    const handler = window.cpc.addToCartHandler as EventListener | undefined;
    if (handler) alpineButton.removeEventListener("click", handler, true);
    try {
      alpineButton.click();
    } finally {
      if (handler) alpineButton.addEventListener("click", handler, true);
    }
  } else if (typeof formm.requestSubmit === "function") {
    formm.requestSubmit();
  } else {
    formm.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  }

  // Hide our loader — theme takes over its own loading state from here
  $(".loading-anim-cpc").hide();
}
